import React, { useEffect } from 'react';
import { styled } from '@mui/system';
import { Grid, Typography, FormHelperText, IconButton, Box, Button } from '@mui/material';
import { FormikErrors, FormikHandlers, FormikHelpers, FormikValues } from 'formik';
import Divider from '../../../../components/Divider';

import { CreateCarrierPayload } from '../../../../store/reducers/Carrier/Type';
import SbTextField from '@Components/Textfield';
import DragDrop from '@Components/File/DragDrop';
import { FileResultType } from '../../../../Helper/file';
import { SbSwitch } from '@Components/Switch';
import SbTooltip from '@Components/Tooltip';
import { InfoRounded, Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import Image from '@Components/Image';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@Store';
import { getSystemList } from '@Reducers/Systems';
import SBDropdown from '@Components/Dropdown';

interface CreateCarrierFormProps {
  formType?: 'create' | 'edit';
  values: FormikValues;
  handleChange: FormikHandlers['handleChange'];
  setFieldValue: FormikHelpers<CreateCarrierPayload>['setFieldValue'];
  errors?: FormikErrors<CreateCarrierPayload>;
}

export const initCreateCarrier: CreateCarrierPayload = {
  name: '',
  code: '',
  logo: null,
  isAccountHolder: false,
  api_key: '',
  secret_key: '',
  _system: [],
  accountNo: '',
  pin: '',
  entity: '',
  d365AccountDisplayValue: '',
  vendorLocationMap: [],
};

function CreateCarrier(props: CreateCarrierFormProps) {
  const { formType, values, errors, handleChange, setFieldValue } = props;
  const dispatch = useDispatch<AppDispatch>();

  const onFileChange = (fileData: FileResultType[]) => {
    setFieldValue('logo', fileData[0]);
  };

  useEffect(() => {
    if (values.isAccountHolder) dispatch(getSystemList());
  }, [values.isAccountHolder]);

  const codeList = [
    'dhl',
    'fedex',
    'dpd',
    'aramex',
    'cirro',
    'bluedart',
    'shree_nandan_courier_limited',
    'universal',
    'ithinklogistics',
    'ithink_logistics_domestic',
    'delhivery',
    'linex',
    'emirates_skycargo',
    'trackon',
  ];

  // Vendor Location Map handlers
  const addVendorLocation = () => {
    const currentVendorLocations = values.vendorLocationMap || [];
    setFieldValue('vendorLocationMap', [...currentVendorLocations, { name: '', locationId: '' }]);
  };

  const removeVendorLocation = (index: number) => {
    const currentVendorLocations = [...(values.vendorLocationMap || [])];
    currentVendorLocations.splice(index, 1);
    setFieldValue('vendorLocationMap', currentVendorLocations);
  };

  const updateVendorLocation = (index: number, field: 'name' | 'locationId', value: string) => {
    const currentVendorLocations = [...(values.vendorLocationMap || [])];
    if (!currentVendorLocations[index]) {
      currentVendorLocations[index] = { name: '', locationId: '' };
    }
    currentVendorLocations[index] = {
      ...currentVendorLocations[index],
      [field]: value,
    };
    setFieldValue('vendorLocationMap', currentVendorLocations);
  };

  // Get validation error for a specific field
  const getFieldError = (index: number, fieldName: 'name' | 'locationId') => {
    if (errors?.vendorLocationMap && Array.isArray(errors.vendorLocationMap)) {
      const itemError = errors.vendorLocationMap[index];
      if (itemError && typeof itemError === 'object' && fieldName in itemError) {
        return (itemError as any)[fieldName];
      }
    }
    return undefined;
  };

  // Check if field is required based on other field's value
  const isFieldRequired = (index: number, fieldName: 'name' | 'locationId') => {
    const location = values.vendorLocationMap?.[index];
    if (!location) return false;

    if (fieldName === 'name') {
      return !!location.locationId?.trim();
    } else {
      return !!location.name?.trim();
    }
  };

  useEffect(() => {
    if (!values.isAccountHolder && values.code) {
      setFieldValue('code', '');
    }
  }, [values.isAccountHolder]);

  return (
    <Grid>
      <Grid item xs={12}>
        <Typography className="org-text underline">
          {(formType === 'edit' && 'Edit') || (formType === 'create' && 'Create new')} carrier
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Divider className="mt-3 mb-3" />
      </Grid>

      <Grid item container xs={12} alignItems="center">
        <Grid item xs={12} md={4} lg={3} mb={{ xs: 1 }}>
          <FormLabel className="md-text font-medium">Carrier Name *</FormLabel>
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          <FormContent>
            <SbTextField
              required
              placeholder="Enter Carrier Name"
              name="name"
              value={values.name}
              onChange={handleChange}
              error={errors?.name}
            />
          </FormContent>
        </Grid>
        <Divider className="mt-3 mb-3" />
      </Grid>

      <Grid item container xs={12} alignItems="center">
        <Grid item xs={12} md={4} lg={3} mb={{ xs: 1 }}>
          <FormLabel className="md-text font-medium">Logo *</FormLabel>
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          <FormContent className="w-full">
            {values.logo && typeof values.logo === 'string' ? (
              <Grid container position="relative">
                <Image src={values.logo} alt={''} width={100} style={{ maxHeight: '200px' }} />
                <span
                  style={{ top: 2 }}
                  onClick={() => {
                    setFieldValue('logo', '');
                  }}
                >
                  <DeleteOutlineIcon />
                </span>
              </Grid>
            ) : (
              <DragDrop
                accept="image/*"
                onChange={onFileChange}
                subText={
                  values.logo && typeof values.logo === 'object'
                    ? `${values.logo.fileName} (${values.logo.fileSize})`
                    : 'Supported formats: JPEG, PNG (450x225px, File upto 5 mb)'
                }
                readType="base64"
              />
            )}
            {errors?.logo && (
              <FormHelperText error className="m-0">
                {typeof errors.logo === 'string' ? errors.logo : 'Logo is required'}
              </FormHelperText>
            )}
          </FormContent>
        </Grid>
        <Divider className="mt-3 mb-3" />
      </Grid>

      <Grid item container xs={12} alignItems="center">
        <Grid container item xs={12} md={4} lg={3} mb={{ xs: 1 }} alignItems="center">
          <AccountHolderFormLabel className="md-text font-medium">
            Are you an existing account holder ?
          </AccountHolderFormLabel>
          <SbTooltip placement="bottom-start" title="Select this option if you need integrations with this carrier.">
            <InfoRounded />
          </SbTooltip>
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          <SbSwitch
            label=""
            onChange={handleChange}
            name="isAccountHolder"
            value={values.isAccountHolder}
            defaultChecked={values.isAccountHolder}
          />
        </Grid>
        <Divider className="mt-3 mb-3" />
      </Grid>

      {values.isAccountHolder && (
        <>
          <Grid item container xs={12} alignItems="center">
            <Grid item xs={12} md={4} lg={3} mb={{ xs: 1 }}>
              <FormLabel className="md-text font-medium">Carrier Code *</FormLabel>
            </Grid>
            <Grid item xs={12} md={8} lg={9}>
              <FormContent>
                <SBDropdown
                  id="code"
                  placeholder="Select Code"
                  options={codeList.map(code => ({ display: code.toUpperCase(), value: code }))}
                  name="code"
                  onChange={value => {
                    setFieldValue('code', value[0]);
                  }}
                  value={values.code ? [values.code] : []}
                  required
                  error={errors?.code as string}
                />
              </FormContent>
            </Grid>
          </Grid>
          <Divider className="mt-3 mb-3" />
        </>
      )}

      {/* New Optional Fields Section */}
      <Grid item container xs={12} alignItems="center">
        <Grid item xs={12} md={4} lg={3} mb={{ xs: 1 }}>
          <FormLabel className="md-text font-medium">D365 Account Display Value</FormLabel>
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          <FormContent>
            <SbTextField
              placeholder="Enter D365 Account Display Value"
              name="d365AccountDisplayValue"
              value={values.d365AccountDisplayValue || ''}
              onChange={handleChange}
              error={errors?.d365AccountDisplayValue}
            />
          </FormContent>
        </Grid>
        <Divider className="mt-3 mb-3" />
      </Grid>

      {/* Vendor Location Map Section */}
      <Grid item container xs={12} alignItems="flex-start">
        <Grid item xs={12} md={4} lg={3} mb={{ xs: 1 }}>
          <FormLabel className="md-text font-medium">Vendor Location Map</FormLabel>
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          <FormContent>
            <Box sx={{ width: '100%' }}>
              {/* Vendor Location Fields */}
              {(values.vendorLocationMap || []).map((location: { name: string; locationId: string }, index: number) => (
                <Box
                  key={index}
                  sx={{
                    p: 2.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    position: 'relative',
                    mb: 2,
                    backgroundColor: 'background.paper',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    '&:hover': {
                      borderColor: 'primary.light',
                    },
                  }}
                >
                  {/* Delete Button - Improved UI */}
                  <IconButton
                    size="small"
                    onClick={() => removeVendorLocation(index)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'error.light',
                      color: 'error.contrastText',
                      width: 28,
                      height: 28,
                      '&:hover': {
                        backgroundColor: 'error.main',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <SbTextField
                        placeholder="Enter Name"
                        label="Name"
                        value={location.name || ''}
                        onChange={e => updateVendorLocation(index, 'name', e.target.value)}
                        error={getFieldError(index, 'name')}
                        required={isFieldRequired(index, 'name')}
                        helperText={isFieldRequired(index, 'name') ? 'Required because Location ID is filled' : ''}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SbTextField
                        label="Location ID"
                        placeholder="Enter Location ID"
                        value={location.locationId || ''}
                        onChange={e => updateVendorLocation(index, 'locationId', e.target.value)}
                        error={getFieldError(index, 'locationId')}
                        required={isFieldRequired(index, 'locationId')}
                        helperText={isFieldRequired(index, 'locationId') ? 'Required because Name is filled' : ''}
                      />
                    </Grid>
                  </Grid>

                  {/* Optional Note */}
                  {!location.name && !location.locationId && (
                    <FormHelperText sx={{ mt: 1, mb: 0, color: 'text.secondary' }}>
                      Both fields are optional, but if you fill one, the other becomes required
                    </FormHelperText>
                  )}
                </Box>
              ))}

              {/* Add Button - Improved UI */}
              <Button
                onClick={addVendorLocation}
                variant="outlined"
                startIcon={<AddIcon />}
                size="small"
                sx={{
                  mt: 1,
                  borderStyle: 'dashed',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'primary.lighter',
                  },
                }}
              >
                Add Vendor Location
              </Button>

              {/* Empty State */}
              {(!values.vendorLocationMap || values.vendorLocationMap.length === 0) && (
                <Box
                  sx={{
                    mt: 2,
                    p: 3,
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    textAlign: 'center',
                    backgroundColor: 'action.hover',
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    No vendor locations added yet
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                    Click "Add Vendor Location" to add a new entry
                  </Typography>
                </Box>
              )}
            </Box>
          </FormContent>
        </Grid>
        <Divider className="mt-3 mb-3" />
      </Grid>
    </Grid>
  );
}

const FormLabel = styled(Typography)`
  min-width: 280px;
`;

const AccountHolderFormLabel = styled(Typography)`
  width: 200px;
  textwrap: balance;
`;

const FormContent = styled('div')`
  max-width: 512px;
`;

export default CreateCarrier;
